import { debounce, pauseAllMedia, removeTrapFocus, trapFocus } from "../lib/ui-helpers.ts";
import { defineDeferredMedia } from "./deferred-media.ts";


type SliderComponentElement = HTMLElement & {
  slider: HTMLElement;
  sliderItems: NodeListOf<HTMLElement>;
  sliderItemsToShow: HTMLElement[];
  enableSliderLooping: boolean;
  currentPageElement: HTMLElement | null;
  pageTotalElement: HTMLElement | null;
  prevButton: HTMLButtonElement | null;
  nextButton: HTMLButtonElement | null;
  sliderItemOffset: number;
  slidesPerPage: number;
  totalPages: number;
  currentPage: number;
  slideScrollPosition: number;
  initPages: () => void;
  resetPages: () => void;
  update: () => void;
  isSlideVisible: (element?: HTMLElement | null, offset?: number) => boolean;
};
const defineVisionneuseCustomElements = () => {
  defineDeferredMedia();

  if (!customElements.get("slider-component")) {
    class SliderComponent extends HTMLElement {
      slider: HTMLElement | null;
      sliderItems: NodeListOf<HTMLElement>;
      sliderItemsToShow: HTMLElement[];
      enableSliderLooping: boolean;
      currentPageElement: HTMLElement | null;
      pageTotalElement: HTMLElement | null;
      prevButton: HTMLButtonElement | null;
      nextButton: HTMLButtonElement | null;
      sliderItemOffset: number;
      slidesPerPage: number;
      totalPages: number;
      currentPage: number;
      slideScrollPosition: number;
      constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector(".slider-counter--current");
        this.pageTotalElement = this.querySelector(".slider-counter--total");
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');
        this.sliderItemsToShow = [];
        this.sliderItemOffset = 0;
        this.slidesPerPage = 0;
        this.totalPages = 0;
        this.currentPage = 1;
        this.slideScrollPosition = 0;
        if (!this.slider || !this.nextButton) return;
        this.initPages();
        const resizeObserver = new ResizeObserver(() => this.initPages());
        resizeObserver.observe(this.slider);
        this.slider.addEventListener("scroll", this.update.bind(this));
        if (!this.hasAttribute("data-external-nav")) {
          this.prevButton?.addEventListener("click", this.onButtonClick.bind(this));
          this.nextButton?.addEventListener("click", this.onButtonClick.bind(this));
        }
      }
      initPages() {
        if (!this.slider) return;
        this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
        const sliderButtons = this.querySelector(".slider-buttons");
        if (sliderButtons) sliderButtons.classList.remove("hidden");
        if (this.sliderItemsToShow.length > 1) {
          this.sliderItemOffset = (this.sliderItemsToShow[1]?.offsetLeft ?? 0) - (this.sliderItemsToShow[0]?.offsetLeft ?? 0);
          this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0]?.offsetLeft) / this.sliderItemOffset);
          this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage;
          this.update();
        } else {
          if (sliderButtons && !this.hasAttribute("data-external-nav")) sliderButtons.classList.add("hidden");
        }
      }
      resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
      }
      update() {
        const slider = this.slider;
        if (!slider || !this.nextButton || !this.sliderItemOffset) return;
        const previousPage = this.currentPage;
        this.currentPage = Math.round(slider.scrollLeft / this.sliderItemOffset) + 1;
        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = String(this.currentPage);
          this.pageTotalElement.textContent = String(this.totalPages);
        }
        if (this.currentPage !== previousPage) {
          this.dispatchEvent(
            new CustomEvent("slideChanged", {
              detail: {
                currentPage: this.currentPage,
                currentElement: this.sliderItemsToShow[this.currentPage - 1],
              },
            }),
          );
        }
        if (this.enableSliderLooping) return;
        if ((this.isSlideVisible(this.sliderItemsToShow[0]) && slider.scrollLeft === 0) || this.currentPage > this.totalPages) {
          this.prevButton?.setAttribute("disabled", "disabled");
        } else {
          this.prevButton?.removeAttribute("disabled");
        }
        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
          this.nextButton?.setAttribute("disabled", "disabled");
        } else {
          this.nextButton?.removeAttribute("disabled");
        }
      }
      isSlideVisible(element?: HTMLElement | null, offset = 0) {
        if (!this.slider) return true;
        const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
        if (!element) return true;
        return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
      }
      onButtonClick(event: Event) {
        event.preventDefault();
        if (!this.slider) return;
        const target = event.currentTarget as HTMLButtonElement | null;
        if (!target) return;
        const step = Number(target.dataset.step || 1);
        const isNext = target.name === "next";
        this.slideScrollPosition = isNext ? this.slider.scrollLeft + step * this.sliderItemOffset : this.slider.scrollLeft - step * this.sliderItemOffset;
        this.slider.scrollTo({ left: this.slideScrollPosition });
      }
    }
    customElements.define("slider-component", SliderComponent);
  }
  if (!customElements.get("product-modal")) {
    class ProductDialog extends HTMLElement {
      openedBy?: HTMLElement;
      moved?: boolean;
      private removeTrapFocusSafe = (elementToFocus?: HTMLElement | null): void => {
        (removeTrapFocus as unknown as (el?: HTMLElement | null) => void)(elementToFocus);
      };
      constructor() {
        super();
        this.querySelector('[id^="ModalClose-"]')?.addEventListener("click", this.hide.bind(this));
        this.addEventListener("keyup", (event: KeyboardEvent) => {
          if (event.code?.toUpperCase() === "ESCAPE") this.hide();
        });
        if (this.classList.contains("media-modal")) {
          this.addEventListener("pointerup", (event) => {
            const e = event as PointerEvent;
            const target = e.target as Element | null;
            if (e.pointerType === "mouse" && target && !target.closest("deferred-media, product-model")) this.hide();
          });
        }
      }
      connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        document.body.appendChild(this);
      }
      show(opener: HTMLElement) {
        this.openedBy = opener as HTMLElement;
        document.body.classList.add("--no-scroll");
        this.setAttribute("open", "");
        const dialog = this.querySelector('[role="dialog"]');
        trapFocus(this, dialog instanceof HTMLElement ? dialog : null);
        pauseAllMedia();
      }
      hide() {
        document.body.classList.remove("--no-scroll");
        this.removeAttribute("open");
        this.removeTrapFocusSafe(this.openedBy ?? null);
        pauseAllMedia();
      }
    }
    customElements.define("product-modal", ProductDialog);
  }
  if (!customElements.get("modal-opener")) {
    class ModalOpener extends HTMLElement {
      constructor() {
        super();
        const button = this.querySelector("button");
        if (!button) return;
        button.addEventListener("click", () => {
          const modalSelector = this.getAttribute("data-modal");
          const modal = modalSelector ? (document.querySelector(modalSelector) as HTMLElement | null) : null;
          const img = button.parentElement?.querySelector?.(".product__media img") as HTMLImageElement | null;
          if (img && modal) {
            const selectedImgUrl = img.src;
            const cutAt = selectedImgUrl.indexOf("&width=");
            const selectedImgUrlAdjust = cutAt > -1 ? selectedImgUrl.substring(0, cutAt) : selectedImgUrl;
            const width = Math.ceil(modal.getBoundingClientRect().width);
            const imgContent = `<picture>
              <source media="(min-width:1200px)" srcset="${selectedImgUrlAdjust}&width=1920, ${selectedImgUrlAdjust}&width=2880 1.5x">
              <source media="(min-width:990px)" srcset="${selectedImgUrlAdjust}&width=1200, ${selectedImgUrlAdjust}&width=1800 1.5x">
              <source media="(min-width:750px)" srcset="${selectedImgUrlAdjust}&width=990, ${selectedImgUrlAdjust}&width=1485 1.5x">
              <source media="(min-width:521px)" srcset="${selectedImgUrlAdjust}&width=750, ${selectedImgUrlAdjust}&width=1120 1.5x">
              <source media="(max-width:520px)" srcset="${selectedImgUrlAdjust}&width=520, ${selectedImgUrlAdjust}&width=775 1.5x">
              <source media="(max-width:320px)" srcset="${selectedImgUrlAdjust}&width=320, ${selectedImgUrlAdjust}&width=480 1.5x">
              <img src="${selectedImgUrlAdjust}&width=${width}" alt="${img.alt || ""}" class="media">
            </picture>`;
            const content = modal.querySelector(".product-media-modal__content") as HTMLElement | null;
            content?.replaceChildren();
            content?.insertAdjacentHTML("afterbegin", imgContent);
          }
          const maybeModal = modal as unknown as {
            show?: (opener: HTMLElement) => void;
          };
          if (maybeModal?.show) maybeModal.show(button);
        });
      }
    }
    customElements.define("modal-opener", ModalOpener);
  }
  if (!customElements.get("media-gallery")) {
    customElements.define(
      "media-gallery",
      class MediaGallery extends HTMLElement {
        elements: {
          liveRegion: HTMLElement | null;
          viewer: SliderComponentElement | null;
          thumbnails: SliderComponentElement | null;
        };
        mql: MediaQueryList;
        constructor() {
          super();
          this.elements = {
            liveRegion: this.querySelector('[id^="GalleryStatus"]'),
            viewer: this.querySelector('[id^="GalleryViewer"]') as SliderComponentElement | null,
            thumbnails: this.querySelector('[id^="GalleryThumbnails"]') as SliderComponentElement | null,
          };
          this.mql = window.matchMedia("(min-width: 750px)");
          if (!this.elements.thumbnails) return;
          this.elements.viewer?.addEventListener("slideChanged", debounce(this.onSlideChanged.bind(this), 100));
          this.elements.thumbnails.querySelectorAll<HTMLElement>("[data-target]").forEach((mediaToSwitch) => {
            const target = mediaToSwitch.dataset.target;
            if (!target) return;
            mediaToSwitch.querySelector("button")?.addEventListener("click", () => this.setActiveMedia(target, false));
          });

          const getMediaOrder = (): string[] =>
            Array.from(this.elements.thumbnails?.querySelectorAll<HTMLElement>("[data-target]") ?? [])
              .map((t) => t.dataset.target)
              .filter(Boolean) as string[];

          const getActiveMediaId = (): string => this.elements.viewer?.querySelector<HTMLElement>("[data-media-id].is-active")?.dataset.mediaId ?? "";

          const updateNavButtons = (order: string[], activeIndex: number) => {
            const prevBtn = this.elements.viewer?.querySelector<HTMLButtonElement>('button[name="previous"]');
            const nextBtn = this.elements.viewer?.querySelector<HTMLButtonElement>('button[name="next"]');
            activeIndex <= 0 ? prevBtn?.setAttribute("disabled", "disabled") : prevBtn?.removeAttribute("disabled");
            activeIndex >= order.length - 1 ? nextBtn?.setAttribute("disabled", "disabled") : nextBtn?.removeAttribute("disabled");
          };

          this.elements.viewer?.querySelector<HTMLButtonElement>('button[name="previous"]')?.addEventListener("click", () => {
            const order = getMediaOrder();
            const activeIndex = order.indexOf(getActiveMediaId());
            const prevId = order[activeIndex - 1];
            if (prevId) {
              this.setActiveMedia(prevId, false);
              updateNavButtons(order, activeIndex - 1);
            }
          });

          this.elements.viewer?.querySelector<HTMLButtonElement>('button[name="next"]')?.addEventListener("click", () => {
            const order = getMediaOrder();
            const activeIndex = order.indexOf(getActiveMediaId());
            const nextId = order[activeIndex + 1];
            if (nextId) {
              this.setActiveMedia(nextId, false);
              updateNavButtons(order, activeIndex + 1);
            }
          });

          window.setTimeout(() => {
            const order = getMediaOrder();
            updateNavButtons(order, order.indexOf(getActiveMediaId()));
          });

          if (this.dataset.desktopLayout !== "stacked" && this.mql.matches) this.removeListSemantic();
        }
        onSlideChanged(event: Event) {
          const e = event as CustomEvent<{ currentElement?: HTMLElement }>;
          if (!this.elements.thumbnails) return;
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${e.detail.currentElement?.dataset.mediaId}"]`);
          this.setActiveThumbnail(thumbnail);
        }
        setActiveMedia(mediaId: string, prepend: boolean) {
          if (!this.elements.viewer) return;
          const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) as HTMLElement | null;
          if (!activeMedia) return;
          this.elements.viewer.querySelectorAll<HTMLElement>("[data-media-id]").forEach((element) => {
            if (element.classList.contains("is-active")) {
              element.classList.remove("is-active");
              element.removeAttribute("mobile-visible");
            }
          });
          activeMedia.classList.add("is-active");
          activeMedia.setAttribute("mobile-visible", "");
          if (prepend) {
            activeMedia.parentElement?.prepend(activeMedia);
            if (this.elements.thumbnails) {
              const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
              activeThumbnail?.parentElement?.prepend(activeThumbnail);
            }
            if (this.elements.viewer?.slider) this.elements.viewer.resetPages();
          }

          this.playActiveMedia(activeMedia);
          if (!this.elements.thumbnails) return;
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          const thumbEl = activeThumbnail as HTMLElement | null;
          this.announceLiveRegion(activeMedia, thumbEl?.dataset.mediaPosition);

          const order = Array.from(this.elements.thumbnails.querySelectorAll<HTMLElement>("[data-target]"))
            .map((t) => t.dataset.target)
            .filter(Boolean) as string[];
          const activeIndex = order.indexOf(mediaId);
          if (activeIndex !== -1) {
            const prevBtn = this.elements.viewer?.querySelector<HTMLButtonElement>('button[name="previous"]');
            const nextBtn = this.elements.viewer?.querySelector<HTMLButtonElement>('button[name="next"]');
            activeIndex <= 0 ? prevBtn?.setAttribute("disabled", "disabled") : prevBtn?.removeAttribute("disabled");
            activeIndex >= order.length - 1 ? nextBtn?.setAttribute("disabled", "disabled") : nextBtn?.removeAttribute("disabled");
          }
        }
        setActiveThumbnail(thumbnail: Element | null) {
          if (!this.elements.thumbnails || !thumbnail) return;
          this.elements.thumbnails.querySelectorAll("button").forEach((element) => element.removeAttribute("aria-current"));
          thumbnail.querySelector("button")?.setAttribute("aria-current", "true");
          const t = thumbnail as HTMLElement;
          if (this.elements.thumbnails.isSlideVisible?.(t, 10)) return;
          this.elements.thumbnails.slider?.scrollTo({ left: t.offsetLeft });
        }
        announceLiveRegion(activeItem: HTMLElement, position?: string) {
          const image = activeItem.querySelector<HTMLImageElement>(".product__modal-opener--image img");
          if (!image || !this.elements.liveRegion) return;
          image.onload = () => {
            const template = window.accessibilityStrings?.imageAvailable;
            if (!template) return;
            const liveRegion = this.elements.liveRegion;
            if (!liveRegion) return;
            liveRegion.setAttribute("aria-hidden", "false");
            liveRegion.innerHTML = template.replace("[index]", position ?? "");
            window.setTimeout(() => {
              liveRegion.setAttribute("aria-hidden", "true");
            }, 2000);
          };
          image.src = image.src;
        }
        playActiveMedia(activeItem: HTMLElement) {
          pauseAllMedia();
          const deferredMedia = activeItem.querySelector("deferred-media, product-model, .deferred-media") as (HTMLElement & { loadContent?: (focus?: boolean) => void }) | null;
          if (!deferredMedia) return;
          const load = () => {
            if (typeof deferredMedia.loadContent === "function") deferredMedia.loadContent(false);
          };
          if (typeof deferredMedia.loadContent === "function") {
            load();
            return;
          }
          const tagName = deferredMedia.tagName?.toLowerCase?.();
          if (!tagName || !window.customElements?.whenDefined) return;
          window.customElements
            .whenDefined(tagName)
            .then(load)
            .catch(() => {});
        }
        removeListSemantic() {
          if (!this.elements.viewer?.slider) return;
          this.elements.viewer.slider.setAttribute("role", "presentation");
          this.elements.viewer.sliderItems?.forEach((slide) => slide.setAttribute("role", "presentation"));
        }
      },
    );
  }
};
const initVisionneuse = (): void => {
  if (!document.querySelector("[data-product-v2-visionneuse]")) return;
  defineVisionneuseCustomElements();
};
export { initVisionneuse };
